import { AlertTriangleIcon, Trash01Icon } from '@langchain/untitled-ui-icons';
import { CircularProgress } from '@mui/joy';

// Removed Joy UI modal components in favor of design system Dialog
import { ReactNode, useId, useState } from 'react';

import { Button, IconButton, Input, Text } from '@/design-system';
import { useGenericDeleteMutation } from '@/hooks/useSwr';

import { Dialog, DialogContent } from './Dialog/Dialog';
import { ExpandableErrorAlert } from './ExpandableErrorAlert';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';

interface DeleteProps {
  invalidationPrefixes?: string[];
  endpoint: string;
  id?: string;
  name?: string;
}

interface DeleteModalProps extends DeleteProps {
  isOpen: boolean;
  modalTitle?: string;
  modalText?: string;
  doClose: () => void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  enhancedConfirmation?: boolean;
}

export function DeleteModal({
  endpoint,
  id,
  name,
  isOpen,
  modalTitle,
  modalText,
  doClose,
  onSuccess,
  onError,
  invalidationPrefixes,
  enhancedConfirmation = false,
}: DeleteModalProps) {
  const deleteMutation = useGenericDeleteMutation(
    { endpoint, id, invalidationPrefixes },
    {
      onSuccess: onSuccess
        ? () => {
            onSuccess();
            doClose();
          }
        : doClose,
      onError,
    }
  );

  const [nameInputVal, setNameInputVal] = useState<string>('');
  const nameInputId = useId();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && doClose()}>
      <DialogContent
        title={
          modalTitle ?? (
            <div className="flex items-center gap-2">
              <AlertTriangleIcon className="size-5 text-error" /> Confirmation
            </div>
          )
        }
        className="w-[420px]"
      >
        <div className="flex flex-col gap-3">
          <Text variant="md" className="text-tertiary">
            {modalText ?? (
              <>
                Are you sure you want to delete
                {name && <span className="font-semibold"> {name}</span>}?
              </>
            )}
          </Text>
        </div>

        {enhancedConfirmation && name && (
          <div className="flex flex-col items-stretch gap-2 py-2">
            <label htmlFor={nameInputId} className="text-xs font-semibold">
              {`To confirm: type ${name} below`}
            </label>
            <Input
              id={nameInputId}
              value={nameInputVal}
              onChange={(value) => setNameInputVal(value)}
              placeholder={`Type ${name} to confirm`}
              size="sm"
            />
          </div>
        )}
        <ExpandableErrorAlert error={deleteMutation.error} />

        <div className="mt-4 flex justify-end gap-2">
          <Button
            size="sm"
            color="secondary"
            variant="outlined"
            onClick={() => doClose()}
            disabled={deleteMutation.isMutating}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              deleteMutation.trigger();
            }}
            loading={deleteMutation.isMutating}
            disabled={
              deleteMutation.isMutating ||
              (enhancedConfirmation && !!name && nameInputVal !== name)
            }
          >
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteConfirmationButton(props: {
  onDelete: () => void;
  title: string;
  description: ReactNode;
  deleteButtonText: string;
  isDeleting: boolean;
  trigger?: ReactNode;
  disabled?: boolean;
}) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleDelete = async () => {
    await props.onDelete();

    setIsConfirmOpen(false);
  };

  return (
    <Popover open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
      <PopoverTrigger asChild>
        {props.trigger ?? (
          <IconButton
            label={props.deleteButtonText}
            icon={Trash01Icon}
            color="error"
          />
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-[400px] p-4">
        <h3 className="mb-2 text-sm font-semibold">{props.title}</h3>
        <p className="mb-4 text-sm">{props.description}</p>
        <div className="flex justify-end gap-2">
          <Button color="secondary" onClick={() => setIsConfirmOpen(false)}>
            Cancel
          </Button>
          {props.isDeleting ? (
            <CircularProgress size="sm" />
          ) : (
            !props.disabled && (
              <Button color="error" onClick={handleDelete}>
                {props.deleteButtonText}
              </Button>
            )
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
